/**
 * api-client.ts — Zentraler openapi-fetch Client (W0.2)
 *
 * Erweiterung W3.1: Demo-Token-Middleware
 * - Im Demo-Modus wird der Demo-API-Token als Authorization-Header gesetzt
 * - Demo-Pruefung via isDemoMode() / getDemoToken() aus demoMode.ts
 *
 * Erweiterung W0.4: Auth-Interceptor (registerRouter, attemptTokenRefresh)
 * - 401-Responses loesen Token-Refresh aus
 * - Bei Refresh-Fehler: clearAuth + queryClient.clear + Redirect zu /login
 *
 * Erweiterung SWEEP7-E3-FE-1 (F-SW7-14): Single-Flight-Refresh + Expiry-Guard
 * - Repro (2026-07-06, live gegen :3927, demo_test@heliumtracker.io):
 *   `/oauth/token` liefert `expires_in: 7200` (2h, siehe doorkeeper.rb:95).
 *   Ohne proaktiven Guard wurde bislang JEDE Navigation, die einen 401 ausloeste,
 *   erneut refresht — bei mehreren parallelen 401s (z.B. Dashboard mit vielen
 *   gleichzeitigen Requests) sogar MEHRFACH, weil `attemptTokenRefresh()`
 *   bislang keinerlei Deduplizierung hatte (Rotation-Race, potenzieller
 *   Logout-Storm falls ein Backend alte Refresh-Tokens hart invalidiert).
 * - Fix: (a) modul-globales In-Flight-Promise dedupliziert alle gleichzeitigen
 *   Aufrufer von `attemptTokenRefresh()` auf GENAU einen `POST /oauth/token`;
 *   (b) `ensureFreshAccessToken()` prueft VOR jedem Request `expiresAt` aus dem
 *   Auth-Store (Buffer 60s) und refresht nur wenn noetig — ein gueltiger Token
 *   wird wiederverwendet, es wird NICHT mehr pro Navigation refresht.
 * - Der reaktive 401-Fallback (inkl. X-Retry-Loop-Schutz) bleibt unveraendert
 *   bestehen und nutzt dieselbe single-flight `attemptTokenRefresh()`.
 */
import createClient from "openapi-fetch";
import type { paths } from "@heliumtracker/api-types";
import { isDemoMode, getDemoToken } from "../services/demoMode";
import { useAuthStore } from "../stores/authStore";
import { ApiError } from "./errors/ApiError";
import { notifyRateLimited } from "./rate-limit-notice";

// ---------------------------------------------------------------------------
// Router-Integration fuer Logout-Redirect (W0.4)
// ---------------------------------------------------------------------------

type NavigateFn = (opts: { to: string }) => void
let routerNavigate: NavigateFn | null = null

export function registerRouter(navigateFn: NavigateFn): void {
  routerNavigate = navigateFn
}

// ---------------------------------------------------------------------------
// Token-Refresh (W0.4)
// ---------------------------------------------------------------------------

const OAUTH_TOKEN_URL = `${import.meta.env.VITE_API_BASE_URL}/oauth/token`

// Single-Flight-Guard: alle gleichzeitigen Aufrufer teilen sich EIN
// In-Flight-Promise, damit bei N parallelen 401s (oder parallelem proaktivem
// Guard + reaktivem Retry) nur genau EIN `POST /oauth/token` ausgeloest wird.
let inFlightRefresh: Promise<string | null> | null = null

/**
 * @param signal Optionales AbortSignal — bricht den `/oauth/token`-Request ab
 *   (FE-ROB-03: der Bootstrap in main.tsx setzt darueber sein 5s-Timeout durch;
 *   ohne das lief ein haengender Request bis zum Browser-Default von ~300s und
 *   die App renderte in dieser Zeit ueberhaupt nichts).
 */
export async function attemptTokenRefresh(signal?: AbortSignal): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh

  inFlightRefresh = doTokenRefresh(signal)
  try {
    return await inFlightRefresh
  } finally {
    // Erst NACH Abschluss freigeben, damit spaetere (nicht-gleichzeitige)
    // Aufrufe wieder einen frischen Refresh ausloesen koennen.
    inFlightRefresh = null
  }
}

async function doTokenRefresh(signal?: AbortSignal): Promise<string | null> {
  // "Angemeldet bleiben": useAuthStore.getRefreshToken() faellt selbst auf
  // localStorage zurueck, falls sessionStorage (neuer Tab/Browser-Neustart)
  // leer ist — REFRESH_KEY hier nicht mehr direkt aus sessionStorage lesen.
  const refreshToken = useAuthStore.getState().getRefreshToken()
  if (!refreshToken) return null

  const clientId = import.meta.env.VITE_OAUTH_CLIENT_ID
  const clientSecret = import.meta.env.VITE_OAUTH_CLIENT_SECRET
  // F-K.1: ohne konfigurierte OAuth-Client-Credentials schlaegt Doorkeeper den
  // Request mit 400 (invalid_client) fehl — lieber gar nicht erst senden.
  if (!clientId || !clientSecret) return null

  try {
    const res = await fetch(OAUTH_TOKEN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: clientId,
        client_secret: clientSecret,
      }),
      // FE-ROB-03: Abbruchsignal durchreichen (undefined = kein Timeout)
      signal,
    })

    if (!res.ok) return null

    const json = await res.json() as {
      access_token?: string
      refresh_token?: string
      expires_in?: number
    }
    const newAccess = json.access_token
    const newRefresh = json.refresh_token

    if (!newAccess) return null

    // Store-Import lazy um zirkulaere Abhaengigkeit zu vermeiden
    const { useAuthStore } = await import('../stores/authStore')
    useAuthStore.getState().setTokens(newAccess, newRefresh, json.expires_in)

    return newAccess
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Expiry-Guard (SWEEP7-E3-FE-1)
// Wird von der Auth-Middleware VOR jedem Request aufgerufen. Refresht nur
// wenn `expiresAt` fehlt oder in < 60s liegt (isAccessTokenFresh-Buffer);
// ein gueltiger Access-Token wird sonst unveraendert wiederverwendet.
// ---------------------------------------------------------------------------

export async function ensureFreshAccessToken(): Promise<void> {
  if (isDemoMode()) return

  const { accessToken, isAccessTokenFresh } = useAuthStore.getState()
  if (!accessToken) return // nicht eingeloggt — nichts zu refreshen

  if (isAccessTokenFresh()) return // gueltig genug — kein Refresh noetig

  await attemptTokenRefresh()
}

// ---------------------------------------------------------------------------
// Auth-Token-Getter
// Prueft zuerst Demo-Modus, dann regulaeren Access-Token.
// ---------------------------------------------------------------------------

export function getAuthToken(): string | null {
  if (isDemoMode()) return getDemoToken()

  // Direkt-Zugriff auf authStore (kein require — Vite/ESM-Browser-kompatibel)
  return useAuthStore.getState().accessToken ?? null
}

// ---------------------------------------------------------------------------
// openapi-fetch Client mit Auth-Middleware + 401-Refresh-Interceptor (AC-4)
// ---------------------------------------------------------------------------

const apiClient = createClient<paths>({
  baseUrl: import.meta.env.VITE_API_BASE_URL,
})

// Middleware 1: Authorization-Header fuer jeden Request
// F011-Fix: Guard gegen leere Token, damit keine malformed "Bearer " Header gesendet werden
// die der Backend als 401 behandeln wuerde (ohne Auth -> 0 Hotspots)
apiClient.use({
  async onRequest({ request }) {
    // SWEEP7-E3-FE-1: proaktiver Expiry-Guard VOR dem Setzen des Headers —
    // refresht nur bei (nahem) Ablauf, sonst wird der bestehende, gueltige
    // Access-Token unveraendert wiederverwendet (kein Refresh pro Navigation).
    await ensureFreshAccessToken()

    const token = getAuthToken()
    // Nur setzen wenn token existiert UND nicht leer (leerer String wuerde als "Bearer " gesendet)
    if (token && token.length > 0) {
      request.headers.set('Authorization', `Bearer ${token}`)
    }
    return request
  },
})

/**
 * FE-ROB-01: Zentrale Fehlerbehandlung fuer JEDE Antwort — die urspruengliche
 * und die nach einem 401-Refresh wiederholte.
 *
 * Vorher lief die Retry-Antwort an dieser Pruefung vorbei (nackter `fetch`,
 * dessen Response direkt zurueckgegeben wurde). Ergebnis: 402 erzeugte keine
 * ApiError (Premium-Gates zeigten generische Fehler statt Upgrade-Karte),
 * 429 loeste keine Rate-Limit-Meldung aus und React Query retried weiter,
 * 422 verlor die Feldfehler in Formularen.
 *
 * Wirft eine ApiError bei HTTP >= 400 (ausser 401 — das behandelt der
 * Refresh-Interceptor selbst).
 */
async function buildApiError(response: Response): Promise<ApiError> {
  let body: unknown
  try {
    body = await response.clone().json()
  } catch {
    body = await response.clone().text()
  }
  return new ApiError(response.status, body, response.headers)
}

async function throwIfErrorResponse(response: Response): Promise<void> {
  if (response.status < 400 || response.status === 401) return

  const error = await buildApiError(response)

  // N1(a): 429 zentral melden — genau EINE globale, nicht stapelnde
  // Meldung pro Cooldown-Fenster. Kein Auto-Retry (siehe query-client.ts),
  // damit der Stundenzaehler nicht weiter belastet wird.
  if (error.isRateLimit) {
    notifyRateLimited(error.retryAfterSeconds)
  }

  throw error
}

// Middleware 2: 401-Refresh-Interceptor + F012 Error-Throwing
// F012: HTTP >= 400 wird als ApiError geworfen (ausser bei 401, das wird refreshed).
// Bei Refresh-Fehler: clearAuth + queryClient.clear + Redirect zu /login.
//
// WICHTIG: request.body ist bei PATCH/POST nach dem ersten fetch konsumiert.
// Wir muessen den Body VOR dem fetch cachen (via clone) damit der Retry
// die gleiche Payload schicken kann.
apiClient.use({
  async onRequest({ request }) {
    // Body fuer potentiellen Retry sichern (nur PATCH/POST/PUT)
    if (['PATCH', 'POST', 'PUT'].includes(request.method)) {
      try {
        const cloned = request.clone()
        const bodyText = await cloned.text()
        ;(request as Request & { __ht_body?: string }).__ht_body = bodyText
      } catch {
        // ignore — body wird nicht gecached
      }
    }
    return request
  },

  async onResponse({ response, request }) {
    // F012: HTTP >= 400 wird als ApiError geworfen (vor dem 401-Handling)
    await throwIfErrorResponse(response)

    if (response.status !== 401) return response

    // Oeffentliche Seiten (seit 09.2026 die Hotspot-Detailseite) werden auch
    // ohne Anmeldung gerendert. Dort laufen einzelne besitzerbezogene Abfragen
    // erwartungsgemaess in ein 401 — das ist kein abgelaufenes Login, sondern
    // gar keins. Ohne diesen Ausstieg lief so ein 401 in den Refresh, der
    // mangels Refresh-Token scheiterte, und der Besucher landete auf /login,
    // obwohl die Seite oeffentlich ist. Am Produktivsystem beobachtet.
    //
    // Massgeblich ist der Zugriffstoken: die Auth-Middleware setzt den
    // Authorization-Header nur, wenn einer da ist. Ein 401 auf einen Request,
    // der gar keine Anmeldedaten getragen hat, kann keine abgelaufene Sitzung
    // sein — ein Refresh koennte daran nichts aendern.
    if (!getAuthToken()) throw await buildApiError(response)

    // Loop-Schutz: kein weiterer Refresh wenn bereits X-Retry gesetzt.
    // ROOT-CAUSE-FIX (P1 /people-Haenger): ein roh zurueckgegebener 401-
    // Response wird von openapi-fetch als PLAIN BODY (kein ApiError) an
    // React Query durchgereicht. shouldRetryQuery() (query-client.ts) prueft
    // `instanceof ApiError` und retried alles andere per Default — eine
    // Route mit mehreren gleichzeitigen Queries (z.B. /people: contacts,
    // hosts, host_split_rules, settlements) summiert dadurch pro Query bis zu
    // MAX_QUERY_RETRIES Retries mit exponentiellem Backoff auf, bevor die
    // (bereits synchron ausgeloeste) Weiterleitung zu /login sichtbar wird —
    // das erklaert das "haengt busy, wirft dann auf /login" auf Seiten mit
    // vielen parallelen Queries. Fix: JEDE terminale 401-Antwort wirft eine
    // echte ApiError, damit shouldRetryQuery sie korrekt als "nicht retrybar"
    // erkennt.
    if (request.headers.get('X-Retry')) throw await buildApiError(response)

    const newToken = await attemptTokenRefresh()

    if (newToken) {
      // Original-Request mit neuem Token wiederholen (einmalig)
      // Body aus dem cache nehmen (im onRequest gesichert)
      const cachedBody = (request as Request & { __ht_body?: string }).__ht_body

      const retryHeaders = new Headers(request.headers)
      retryHeaders.set('Authorization', `Bearer ${newToken}`)
      retryHeaders.set('X-Retry', '1')

      const retryInit: RequestInit = {
        method: request.method,
        headers: retryHeaders,
        credentials: request.credentials,
        mode: request.mode,
      }
      if (cachedBody && cachedBody.length > 0) {
        retryInit.body = cachedBody
      }

      const retryResponse = await fetch(request.url, retryInit)

      // FE-ROB-01: Die Retry-Antwort MUSS dieselbe Fehlerbehandlung durchlaufen
      // wie die Erstantwort — sonst umgeht sie ApiError/429-Handling komplett.
      await throwIfErrorResponse(retryResponse)
      // Retry trotz frischem Token wieder 401 (throwIfErrorResponse ueberspringt
      // 401 bewusst) — auch das muss als ApiError ankommen, siehe Root-Cause-
      // Fix-Kommentar oben.
      if (retryResponse.status === 401) throw await buildApiError(retryResponse)

      return retryResponse
    }

    // Refresh gescheitert — Logout-Cleanup
    const { useAuthStore } = await import('../stores/authStore')
    useAuthStore.getState().clearAuth()

    const { queryClient } = await import('./query-client')
    queryClient.clear()

    if (routerNavigate) {
      routerNavigate({ to: '/login' })
    }

    throw await buildApiError(response)
  },
})

export default apiClient;
